import { COUNTRY_CONFIGURATION, FILTER_FEATURE } from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { TravelSimRegistrationRequestModel } from "features/travelSimRegistration/data/model/TravelSimRegistrationRequestModel";
import { TravelSimRegistrationResponseModel } from "features/travelSimRegistration/data/model/TravelSimRegistrationResponseModel";
import { TravelSimRegistrationInjection } from "features/travelSimRegistration/di/TravelSimRegistrationInjection";
import { ExportPayload } from "features/travelSimRegistration/domain/entities/TravelSimRegistrationPayload";
import {
  SearchContext,
  SearchFormValues,
  TravelSimRegistrationQuery,
} from "features/travelSimRegistration/domain/entities/TravelSimRegistrationQuery";
import { ITravelSimRegistrationRepository } from "features/travelSimRegistration/domain/repositories/ITravelSimRegistrationRepository";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { DateFormat } from "utils/format/DateFormat";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";

@injectable()
export class TravelSimRegistrationUseCase {
  private travelSimRegistrationRepo: ITravelSimRegistrationRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.travelSimRegistrationRepo =
      TravelSimRegistrationInjection.getTravelSimRegistrationRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  private prepareFormValues(
    rawFormValues: Record<string, unknown>,
  ): SearchFormValues {
    return {
      status: paramsChecking(rawFormValues?.status, "select") as
        | number
        | string,
      code: paramsChecking(rawFormValues?.code, "input") as string,
      transRef: paramsChecking(rawFormValues?.transRef, "input") as string,
      supplierRefCode: paramsChecking(
        rawFormValues?.supplierRefCode,
        "input",
      ) as string,
      packageCode: paramsChecking(
        rawFormValues?.packageCode,
        "select",
      ) as string[],
      simType: paramsChecking(rawFormValues?.simType, "select") as
        | number
        | string,
      carrierCode: paramsChecking(rawFormValues?.carrier, "select") as string[],
      nation: paramsChecking(rawFormValues?.nation, "select") as string,
      phoneNumber: paramsChecking(
        rawFormValues?.phoneNumber,
        "input",
      ) as string,
      buyerPhone: paramsChecking(rawFormValues?.buyerPhone, "input") as string,
      partnerCode: paramsChecking(
        rawFormValues?.partnerCode,
        "select",
      ) as string,
      userRef: (rawFormValues?.userRef as string[]) || undefined,
      serial: paramsChecking(rawFormValues?.serial, "input") as string,
      storeCode: paramsChecking(rawFormValues?.storeCode, "select") as string,
    };
  }

  private getDateRange(rawFormValues: Record<string, unknown>): {
    dateFrom: string;
    dateTo: string;
  } {
    const dateFrom =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "from",
      ) ?? "";
    const dateTo =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "to",
      ) ?? "";
    return { dateFrom, dateTo };
  }

  async getInboundList(
    params: TravelSimRegistrationRequestModel,
  ): Promise<ListBaseApiResponseModel<TravelSimRegistrationResponseModel>> {
    return await this.travelSimRegistrationRepo.getInboundList(params);
  }

  async getOutboundList(
    params: TravelSimRegistrationRequestModel,
  ): Promise<ListBaseApiResponseModel<TravelSimRegistrationResponseModel>> {
    return await this.travelSimRegistrationRepo.getOutboundList(params);
  }

  async getInboundListFromSearch(
    rawFormValues: Record<string, unknown>,
    context: SearchContext,
  ): Promise<{
    list: ListBaseApiResponseModel<TravelSimRegistrationResponseModel>;
    params: TravelSimRegistrationRequestModel;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = TravelSimRegistrationQuery.buildSearchQuery(
      formValues,
      context,
      dateFrom,
      dateTo,
    );
    const list = await this.travelSimRegistrationRepo.getInboundList(params);
    return { list, params };
  }

  async getOutboundListFromSearch(
    rawFormValues: Record<string, unknown>,
    context: SearchContext,
  ): Promise<{
    list: ListBaseApiResponseModel<TravelSimRegistrationResponseModel>;
    params: TravelSimRegistrationRequestModel;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = TravelSimRegistrationQuery.buildSearchQuery(
      formValues,
      context,
      dateFrom,
      dateTo,
    );
    const list = await this.travelSimRegistrationRepo.getOutboundList(params);
    return { list, params };
  }

  async getInboundListWithTableChange(
    prevParams: TravelSimRegistrationRequestModel,
    rawFormValues: Record<string, unknown>,
    page: number,
    limit: number,
  ): Promise<{
    list: ListBaseApiResponseModel<TravelSimRegistrationResponseModel>;
    params: TravelSimRegistrationRequestModel;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const params = TravelSimRegistrationQuery.buildTableChangeQuery(
      prevParams,
      formValues,
      page,
      limit,
    );
    const list = await this.travelSimRegistrationRepo.getInboundList(params);
    return { list, params };
  }

  async getOutboundListWithTableChange(
    prevParams: TravelSimRegistrationRequestModel,
    rawFormValues: Record<string, unknown>,
    page: number,
    limit: number,
  ): Promise<{
    list: ListBaseApiResponseModel<TravelSimRegistrationResponseModel>;
    params: TravelSimRegistrationRequestModel;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const params = TravelSimRegistrationQuery.buildTableChangeQuery(
      prevParams,
      formValues,
      page,
      limit,
    );
    const list = await this.travelSimRegistrationRepo.getOutboundList(params);
    return { list, params };
  }

  async getInboundListRedo(): Promise<{
    list: ListBaseApiResponseModel<TravelSimRegistrationResponseModel>;
    params: TravelSimRegistrationRequestModel;
  }> {
    const { fromDate, toDate } = DateFormat.getDateRange(0);
    const params = TravelSimRegistrationQuery.buildRedoQuery(
      fromDate ?? "",
      toDate ?? "",
    );
    const list = await this.travelSimRegistrationRepo.getInboundList(params);
    return { list, params };
  }

  async getOutboundListRedo(): Promise<{
    list: ListBaseApiResponseModel<TravelSimRegistrationResponseModel>;
    params: TravelSimRegistrationRequestModel;
  }> {
    const { fromDate, toDate } = DateFormat.getDateRange(0);
    const params = TravelSimRegistrationQuery.buildRedoQuery(
      fromDate ?? "",
      toDate ?? "",
    );
    const list = await this.travelSimRegistrationRepo.getOutboundList(params);
    return { list, params };
  }

  async getDetail(
    params: TravelSimRegistrationRequestModel,
  ): Promise<BaseApiResponseModel<TravelSimRegistrationResponseModel>> {
    return await this.travelSimRegistrationRepo.getDetail(params);
  }

  async exportInboundReport(
    params: TravelSimRegistrationRequestModel,
  ): Promise<BaseApiResponseModel<Blob>> {
    return await this.travelSimRegistrationRepo.inboundExportReport(params);
  }

  async exportOutboundReport(
    params: TravelSimRegistrationRequestModel,
  ): Promise<BaseApiResponseModel<Blob>> {
    return await this.travelSimRegistrationRepo.outboundExportReport(params);
  }

  async exportInboundReportFromSearch(
    payload: ExportPayload,
  ): Promise<BaseApiResponseModel<Blob>> {
    const formValues = this.prepareFormValues(payload.rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(payload.rawFormValues);
    const params = TravelSimRegistrationQuery.buildExportQuery(
      formValues,
      payload.context,
      dateFrom,
      dateTo,
    );
    return await this.travelSimRegistrationRepo.inboundExportReport(params);
  }

  async exportOutboundReportFromSearch(
    payload: ExportPayload,
  ): Promise<BaseApiResponseModel<Blob>> {
    const formValues = this.prepareFormValues(payload.rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(payload.rawFormValues);
    const params = TravelSimRegistrationQuery.buildExportQuery(
      formValues,
      payload.context,
      dateFrom,
      dateTo,
    );
    return await this.travelSimRegistrationRepo.outboundExportReport(params);
  }

  async getFilterList(
    params: BaseFilterListRequestModel,
    target: "carrier" | "partner" | "store" | "nation" | "package",
  ): Promise<Array<BaseFilterListResponseModel>> {
    let path = "";
    switch (target) {
      case "carrier":
        path = FILTER_FEATURE.CARRIER_LIST;
        break;
      case "partner":
        path = FILTER_FEATURE.PARTNER_LIST;
        break;
      case "store":
        path = FILTER_FEATURE.STORE_LIST;
        break;
      case "nation":
        path = COUNTRY_CONFIGURATION.LIST;
        break;
      case "package":
        path = FILTER_FEATURE.PACKAGE_LIST;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      savedKey:
        target === "nation" ? CacheKeyEnum.nationListForFilter : undefined,
    });
  }

  async getUserForFilter(
    params: BaseUserRequestModel,
    target: "staff" | "userB2B",
  ): Promise<Array<BaseUserResponseModel>> {
    let path = "";
    switch (target) {
      case "staff":
        path = FILTER_FEATURE.STAFF_LIST;
        break;
      case "userB2B":
        path = FILTER_FEATURE.USER_LIST_B2B;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
    });
  }
}
